import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {useMemo, useState} from "react";
import { useHistory } from "react-router-dom";
import AddressCard from "../../components/general/addresses/addressCard";
import AccountSidebar from "../../components/general/orderHistory/accountSidebar";
import { getRouteUrl } from "../../helpers/routing/getRouteUrl";
import {
    ROUTE_ADDRESS_CREATE,
    ROUTE_ADDRESS_EDIT,
    ROUTE_ADDRESSES,
    ROUTE_COUPONS,
    ROUTE_DETAILS,
    ROUTE_ORDER_HISTORY,
    ROUTE_RETURNS,
} from "../../routers/routes";
import { useAddresses } from "../../hooks/address/useAddresses";
import DeleteAddressDialog from "../../components/general/addresses/deleteAddressDialog";
import {useTranslation} from "react-i18next";
import {DASHBOARD} from "../../locales/components/namespaces";
import axios from "axios";
import {API} from "../../helpers/constants";
import {enqueueSnackbar} from "notistack";
import {handleAxiosErrors} from "../../helpers/errorHandling";
import useUserData from "../../hooks/user/useUserData";
import {useDispatch} from "react-redux";
import {userLogout} from "../../actions/currentUser/actions";
import useCartData from "../../hooks/cart/useCartData";

export default function Addresses() {
    const { t } = useTranslation(DASHBOARD);
    const history = useHistory();
    const { token } = useUserData();
    const { addresses, loading,countries } = useAddresses();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const dispatch = useDispatch();

    const getCountryLabel = (id) =>
        countries.find((c) => String(c.id) === String(id))?.Description || "";

    const accountMenuItems = [
        t("accountDetails"),
        t( "orderHistory"),
        t("addresses"),
        t("logout"),
    ];

    const handleLogout = () => {
        dispatch(userLogout());
        history.push(`/`)
    };

    const addressList = useMemo(() => {
        const rawAddresses = addresses?.data?.addresses ?? [];

        return rawAddresses.map((addr, index) => {
            const title =
                String(addr?.isInvoice) === "1"
                    ? "ΣΤΟΙΧΕΙΑ ΤΙΜΟΛΟΓΙΟΥ"
                    : addr?.Comments?.trim() || `ΔΙΕΥΘΥΝΣΗ ${index + 1}`;

            const lines = [
                addr?.Address,
                [addr?.ZipCode, addr?.City].filter(Boolean).join(" "),
                getCountryLabel(addr?.CountryId),
                addr?.Region,
                addr?.AddrPhone,
                addr?.Comments && String(addr?.isInvoice) !== "1" ? addr.Comments : null,
            ].filter(Boolean);

            return {
                id: addr.id,
                title,
                lines,
                isInvoice: String(addr?.isInvoice) === "1",
                raw: addr,
            };
        });
    }, [addresses]);

    const handleOpenDeleteDialog = (addr) => {
        setSelectedAddress(addr);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        if (deleteLoading) return;
        setDeleteDialogOpen(false);
        setSelectedAddress(null);
    };

    const handleConfirmDelete =  () => {
        if (!selectedAddress?.id) return;
        setDeleteLoading(true);
        axios({
            method: "post",
            url: `${API}/en/brew/profile/update`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            data: {
               id:selectedAddress?.id,
            },
        })
            .then(() => {
                enqueueSnackbar(t("Address was successfully deleted!"), {
                    variant: "success",
                    autoHideDuration: 3000,
                });
                setDeleteLoading(false);
            })
            .catch((error) => {
                setDeleteLoading(false);
                enqueueSnackbar(handleAxiosErrors(error, t("An error occurred!")), {
                    variant: "error",
                    autoHideDuration: 3000,
                });
            });
    };

    return (
        <Box
            sx={{
                width: "95%",
                //maxWidth: 1760,
                mx: "auto",
                bgcolor: "#E9E9E9",
                borderRadius: "30px",
                px: { xs: 2, md: 6 },
                py: { xs: 4, md: 6 },
            }}
        >
            <Typography
                sx={{
                    fontFamily: "'Zen Maru Gothic', sans-serif",
                    fontWeight: 900,
                    fontSize: { xs: 24, md: 36 },
                    lineHeight: "110%",
                    textAlign: "center",
                    mb: { xs: 3, md: 4 },
                    color: "#111111",
                }}
            >
                {t("My addresses")}
            </Typography>

            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">
                <Grid item xs={12} md={3}>
                    <AccountSidebar
                        items={accountMenuItems}
                        activeIndex={2}
                        onSelect={(index) => {
                            if (index === 0) history.push(getRouteUrl(ROUTE_DETAILS));
                            if (index === 1) history.push(getRouteUrl(ROUTE_ORDER_HISTORY));
                            if (index === 2) history.push(getRouteUrl(ROUTE_ADDRESSES));
                            if (index === 3) handleLogout();
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={9}>
                    <Grid container spacing={{ xs: 2.5, md: 3 }}>
                        {addressList.map((addr) => (
                            <Grid key={addr.id} item xs={12} md={6}>
                                <AddressCard
                                    title={addr.title}
                                    lines={addr.lines}
                                    onEdit={() =>
                                        history.push(
                                            getRouteUrl(ROUTE_ADDRESS_EDIT, { id: addr.id })
                                        )
                                    }
                                    onDelete={() => handleOpenDeleteDialog(addr)}
                                />
                            </Grid>
                        ))}

                        {!loading && addressList.length === 0 && (
                            <Grid item xs={12}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Zen Maru Gothic', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        color: "#111111",
                                    }}
                                >
                                    {t("There are no stored addresses.")}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Box
                        sx={{
                            mt: { xs: 3, md: 4 },
                            display: "flex",
                            justifyContent: { xs: "center", md: "flex-start" },
                        }}
                    >
                        <Button
                            onClick={() =>
                                history.push({
                                    pathname: getRouteUrl(ROUTE_ADDRESS_CREATE),
                                    state: { countries },
                                })
                            }
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "row",
                                gap: "10px",
                                backgroundColor: "#FCFCFC",
                                borderRadius: "80px",
                                px: "30px",
                                py: "10px",
                                height: "41px",
                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                fontWeight: 900,
                                fontSize: "12px",
                                color: "#E5007E",
                                "&:hover": {
                                    backgroundColor: "#FFE5F3",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontWeight: 900,
                                    fontSize: "12px",
                                    color: "#E5007E",
                                }}
                            >
                                {t('New address')} +
                            </Typography>
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <DeleteAddressDialog
                deleteDialogOpen={deleteDialogOpen}
                handleCloseDeleteDialog={handleCloseDeleteDialog}
                selectedAddress={selectedAddress}
                deleteLoading={deleteLoading}
                handleConfirmDelete={handleConfirmDelete}
                t={t}
            />
        </Box>
    );
}